/**
 * PathEditor — Path/waypoint editing logic for MapEditorScene.
 *
 * Handles: addWaypoint, finishCurrentPath, deleteLastPath, autoGeneratePaths,
 * drawPaths, updatePathListText.
 */
import Phaser from 'phaser';
import { TileType } from '@/config/GameConfig';

export interface PathEditorState {
  paths: { col: number; row: number }[][];
  currentPath: { col: number; row: number }[];
  editingPathIndex: number;
  grid: TileType[][];
  gridCols: number;
  gridRows: number;
  tileSize: number;
  gridOffsetX: number;
  gridOffsetY: number;
}

export function addWaypoint(
  state: PathEditorState,
  row: number,
  col: number,
  setStatus: (text: string) => void,
): void {
  state.currentPath.push({ col, row });
  setStatus(`Waypoint ${state.currentPath.length} at (${col},${row}). ESC to finish.`);
}

export function finishCurrentPath(
  state: PathEditorState,
  setStatus: (text: string) => void,
): void {
  if (state.currentPath.length < 2) {
    setStatus('Path needs at least 2 waypoints.');
    return;
  }
  if (state.editingPathIndex >= 0) {
    state.paths[state.editingPathIndex] = [...state.currentPath];
    state.editingPathIndex = -1;
  } else {
    state.paths.push([...state.currentPath]);
  }
  state.currentPath = [];
  setStatus(`Path saved. Total: ${state.paths.length} path(s).`);
}

export function deleteLastPath(
  state: PathEditorState,
  pushUndo: () => void,
  setStatus: (text: string) => void,
): void {
  if (state.currentPath.length > 0) {
    state.currentPath = [];
    setStatus('Current path cancelled.');
    return;
  }
  if (state.paths.length > 0) {
    pushUndo();
    state.paths.pop();
    setStatus(`Path deleted. ${state.paths.length} remaining.`);
  }
}

export function autoGeneratePaths(
  state: PathEditorState,
  pushUndo: () => void,
  setStatus: (text: string) => void,
): void {
  const spawns: { col: number; row: number }[] = [];
  const bases: { col: number; row: number }[] = [];
  for (let r = 0; r < state.gridRows; r++) {
    for (let c = 0; c < state.gridCols; c++) {
      if (state.grid[r][c] === TileType.SPAWN) spawns.push({ col: c, row: r });
      if (state.grid[r][c] === TileType.BASE) bases.push({ col: c, row: r });
    }
  }

  if (spawns.length === 0 || bases.length === 0) {
    setStatus('Need at least 1 Spawn and 1 Base tile.');
    return;
  }

  pushUndo();

  const walkable = (r: number, c: number): boolean => {
    if (r < 0 || r >= state.gridRows || c < 0 || c >= state.gridCols) return false;
    const t = state.grid[r][c];
    return t === TileType.PATH || t === TileType.SPAWN || t === TileType.BASE ||
           t === TileType.MUD || t === TileType.GRILLE || t === TileType.GROUND;
  };

  const bfs = (start: { col: number; row: number }): { col: number; row: number }[] | null => {
    const visited = new Set<string>();
    const prev = new Map<string, string>();
    const queue: { col: number; row: number }[] = [start];
    const key = (c: number, r: number) => `${c},${r}`;
    visited.add(key(start.col, start.row));

    while (queue.length > 0) {
      const cur = queue.shift()!;
      if (state.grid[cur.row][cur.col] === TileType.BASE) {
        const fullPath: { col: number; row: number }[] = [];
        let k = key(cur.col, cur.row);
        while (k) {
          const [cs, rs] = k.split(',').map(Number);
          fullPath.unshift({ col: cs, row: rs });
          k = prev.get(k)!;
        }
        return fullPath;
      }
      for (const [dr, dc] of [[0, 1], [0, -1], [1, 0], [-1, 0]]) {
        const nr = cur.row + dr;
        const nc = cur.col + dc;
        const nk = key(nc, nr);
        if (walkable(nr, nc) && !visited.has(nk)) {
          visited.add(nk);
          prev.set(nk, key(cur.col, cur.row));
          queue.push({ col: nc, row: nr });
        }
      }
    }
    return null;
  };

  const simplify = (fullPath: { col: number; row: number }[]): { col: number; row: number }[] => {
    if (fullPath.length <= 2) return fullPath;
    const result: { col: number; row: number }[] = [fullPath[0]];
    for (let i = 1; i < fullPath.length - 1; i++) {
      const prv = fullPath[i - 1];
      const cur = fullPath[i];
      const nxt = fullPath[i + 1];
      if (cur.col - prv.col !== nxt.col - cur.col || cur.row - prv.row !== nxt.row - cur.row) {
        result.push(cur);
      }
    }
    result.push(fullPath[fullPath.length - 1]);
    return result;
  };

  const newPaths: { col: number; row: number }[][] = [];
  const failedSpawns: number[] = [];

  for (let i = 0; i < spawns.length; i++) {
    const fullPath = bfs(spawns[i]);
    if (fullPath) {
      newPaths.push(simplify(fullPath));
    } else {
      failedSpawns.push(i + 1);
    }
  }

  state.paths = newPaths;
  state.currentPath = [];

  if (failedSpawns.length > 0) {
    setStatus(`Auto: ${newPaths.length} path(s). No route for spawn(s): ${failedSpawns.join(', ')}.`);
  } else {
    setStatus(`Auto: ${newPaths.length} path(s) from ${spawns.length} spawn(s).`);
  }
}

/**
 * Draw all paths + current path being edited onto a Graphics object.
 */
export function drawPaths(
  pathGfx: Phaser.GameObjects.Graphics,
  state: PathEditorState,
): void {
  pathGfx.clear();
  const ts = state.tileSize;

  const pathColors = [0xffaa00, 0x44ccff, 0xff44aa, 0x44ff44, 0xffff44, 0xaa44ff];
  for (let pi = 0; pi < state.paths.length; pi++) {
    const path = state.paths[pi];
    const color = pathColors[pi % pathColors.length];
    pathGfx.lineStyle(3, color, 0.8);

    for (let i = 0; i < path.length - 1; i++) {
      const ax = state.gridOffsetX + path[i].col * ts + ts / 2;
      const ay = state.gridOffsetY + path[i].row * ts + ts / 2;
      const bx = state.gridOffsetX + path[i + 1].col * ts + ts / 2;
      const by = state.gridOffsetY + path[i + 1].row * ts + ts / 2;
      pathGfx.lineBetween(ax, ay, bx, by);
    }

    pathGfx.fillStyle(color, 1);
    for (let i = 0; i < path.length; i++) {
      const wx = state.gridOffsetX + path[i].col * ts + ts / 2;
      const wy = state.gridOffsetY + path[i].row * ts + ts / 2;
      pathGfx.fillCircle(wx, wy, Math.max(3, ts * 0.15));
    }
  }

  // Current path being edited
  if (state.currentPath.length > 0) {
    pathGfx.lineStyle(3, 0xffffff, 0.6);
    for (let i = 0; i < state.currentPath.length - 1; i++) {
      const ax = state.gridOffsetX + state.currentPath[i].col * ts + ts / 2;
      const ay = state.gridOffsetY + state.currentPath[i].row * ts + ts / 2;
      const bx = state.gridOffsetX + state.currentPath[i + 1].col * ts + ts / 2;
      const by = state.gridOffsetY + state.currentPath[i + 1].row * ts + ts / 2;
      pathGfx.lineBetween(ax, ay, bx, by);
    }
    pathGfx.fillStyle(0xffffff, 1);
    for (const wp of state.currentPath) {
      const wx = state.gridOffsetX + wp.col * ts + ts / 2;
      const wy = state.gridOffsetY + wp.row * ts + ts / 2;
      pathGfx.fillCircle(wx, wy, Math.max(4, ts * 0.2));
    }
  }
}

/**
 * Update the path list text in the toolbar.
 */
export function updatePathListText(
  pathListText: Phaser.GameObjects.Text,
  paths: { col: number; row: number }[][],
): void {
  if (paths.length === 0) {
    pathListText.setText('(none)');
    return;
  }
  const lines = paths.map((p, i) => {
    const start = p[0];
    const end = p[p.length - 1];
    return `#${i + 1}: (${start.col},${start.row})→(${end.col},${end.row}) [${p.length}pt]`;
  });
  pathListText.setText(lines.join('\n'));
}
